// src/services/compPlanService.js

import { tenantDataService } from './tenantDataService';

/**
 * Compensation Plan Service
 * Handles compensation plan management, calculations, and ledger operations
 */
class CompPlanService {
  constructor() {
    this.collectionName = 'compensationPlans';
    this.assignmentsCollection = 'staffCompAssignments';
    this.ledgerCollection = 'compCalcLedger';
    this.payoutsCollection = 'payouts';
    this.payslipsCollection = 'payslips';
  }

  // ==================== COMPENSATION PLANS ====================

  /**
   * Get all compensation plans for the current tenant
   */
  async getCompensationPlans() {
    try {
      return await tenantDataService.getAll(this.collectionName);
    } catch (error) {
      console.error('Error fetching compensation plans:', error);
      throw error;
    }
  }

  /**
   * Get a specific compensation plan by ID
   */
  async getCompensationPlan(planId) {
    try {
      return await tenantDataService.getById(this.collectionName, planId);
    } catch (error) {
      console.error('Error fetching compensation plan:', error);
      throw error;
    }
  }

  /**
   * Create a new compensation plan
   */
  async createCompensationPlan(planData) {
    try {
      const plan = {
        ...planData,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };
      return await tenantDataService.create(this.collectionName, plan);
    } catch (error) {
      console.error('Error creating compensation plan:', error);
      throw error;
    }
  }

  /**
   * Update an existing compensation plan
   */
  async updateCompensationPlan(planId, updates) {
    try {
      const updatedPlan = {
        ...updates,
        updatedAt: new Date()
      };
      return await tenantDataService.update(this.collectionName, planId, updatedPlan);
    } catch (error) {
      console.error('Error updating compensation plan:', error);
      throw error;
    }
  }

  /**
   * Delete a compensation plan
   */
  async deleteCompensationPlan(planId) {
    try {
      // Check if plan is assigned to any staff
      const assignments = await this.getStaffAssignments();
      const hasAssignments = assignments.some(assignment => assignment.planId === planId);
      
      if (hasAssignments) {
        throw new Error('Cannot delete plan that is assigned to staff members');
      }

      return await tenantDataService.delete(this.collectionName, planId);
    } catch (error) {
      console.error('Error deleting compensation plan:', error);
      throw error;
    }
  }

  // ==================== STAFF ASSIGNMENTS ====================

  /**
   * Get all staff compensation assignments
   */
  async getStaffAssignments() {
    try {
      return await tenantDataService.getAll(this.assignmentsCollection);
    } catch (error) {
      console.error('Error fetching staff assignments:', error);
      throw error;
    }
  }

  /**
   * Get compensation assignment for a specific staff member
   */
  async getStaffAssignment(staffId) {
    try {
      const assignments = await this.getStaffAssignments();
      return assignments.find(assignment => assignment.staffId === staffId);
    } catch (error) {
      console.error('Error fetching staff assignment:', error);
      throw error;
    }
  }

  /**
   * Assign a compensation plan to a staff member
   */
  async assignPlanToStaff(staffId, planId, effectiveDate = new Date()) {
    try {
      const assignment = {
        staffId,
        planId,
        effectiveDate,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };

      // Check if assignment already exists
      const existingAssignment = await this.getStaffAssignment(staffId);
      if (existingAssignment) {
        return await tenantDataService.update(this.assignmentsCollection, existingAssignment.id, {
          ...assignment,
          id: existingAssignment.id
        });
      } else {
        return await tenantDataService.create(this.assignmentsCollection, assignment);
      }
    } catch (error) {
      console.error('Error assigning plan to staff:', error);
      throw error;
    }
  }

  /**
   * Remove compensation plan assignment from staff member
   */
  async removeStaffAssignment(staffId) {
    try {
      const assignment = await this.getStaffAssignment(staffId);
      if (assignment) {
        return await tenantDataService.delete(this.assignmentsCollection, assignment.id);
      }
    } catch (error) {
      console.error('Error removing staff assignment:', error);
      throw error;
    }
  }

  // ==================== COMPENSATION CALCULATIONS ====================

  /**
   * Calculate compensation for a staff member based on their assigned plan
   */
  async calculateStaffCompensation(staffId, period = 'current') {
    try {
      const assignment = await this.getStaffAssignment(staffId);
      if (!assignment) {
        return { total: 0, breakdown: [], planType: 'none' };
      }

      const plan = await this.getCompensationPlan(assignment.planId);
      if (!plan) {
        return { total: 0, breakdown: [], planType: 'none' };
      }

      // Get ledger entries for the period
      const ledgerEntries = await this.getLedgerEntries(staffId, period);
      
      return this.calculateCompensationFromLedger(plan, ledgerEntries);
    } catch (error) {
      console.error('Error calculating staff compensation:', error);
      throw error;
    }
  }

  /**
   * Calculate compensation from ledger entries using plan rules
   */
  calculateCompensationFromLedger(plan, ledgerEntries) {
    let total = 0;
    const breakdown = [];

    switch (plan.type) {
      case 'salary':
        total = plan.baseSalary || 0;
        breakdown.push({
          type: 'Base Salary',
          amount: total,
          description: `Monthly salary: $${total}`
        });
        break;

      case 'hourly':
        const totalHours = ledgerEntries
          .filter(entry => entry.type === 'time_logged')
          .reduce((sum, entry) => sum + (entry.hours || 0), 0);
        
        total = totalHours * (plan.hourlyRate || 0);
        breakdown.push({
          type: 'Hourly Pay',
          amount: total,
          description: `${totalHours} hours × $${plan.hourlyRate}/hr`
        });
        break;

      case 'commission':
        const revenue = ledgerEntries
          .filter(entry => entry.type === 'invoice_paid')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0);
        
        total = revenue * ((plan.commissionRate || 0) / 100);
        breakdown.push({
          type: 'Commission',
          amount: total,
          description: `${plan.commissionRate}% of $${revenue} revenue`
        });
        break;

      case 'hybrid':
        // Base salary
        const baseSalary = plan.baseSalary || 0;
        total += baseSalary;
        breakdown.push({
          type: 'Base Salary',
          amount: baseSalary,
          description: `Monthly base: $${baseSalary}`
        });

        // Commission on top
        const hybridRevenue = ledgerEntries
          .filter(entry => entry.type === 'invoice_paid')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0);
        
        const commission = hybridRevenue * ((plan.commissionRate || 0) / 100);
        total += commission;
        breakdown.push({
          type: 'Commission Bonus',
          amount: commission,
          description: `${plan.commissionRate}% of $${hybridRevenue} revenue`
        });
        break;

      default:
        console.warn('Unknown compensation plan type:', plan.type);
    }

    return {
      total: Math.round(total * 100) / 100, // Round to 2 decimal places
      breakdown,
      planType: plan.type,
      planName: plan.name
    };
  }

  // ==================== LEDGER OPERATIONS ====================

  /**
   * Write a ledger entry for compensation tracking
   */
  async writeLedgerEntry(staffId, entryData) {
    try {
      const entry = {
        staffId,
        ...entryData,
        timestamp: new Date(),
        processed: false
      };

      return await tenantDataService.create(this.ledgerCollection, entry);
    } catch (error) {
      console.error('Error writing ledger entry:', error);
      throw error;
    }
  }

  /**
   * Get ledger entries for a staff member and period
   */
  async getLedgerEntries(staffId, period = 'current') {
    try {
      const allEntries = await tenantDataService.getAll(this.ledgerCollection);
      
      // Filter by staff ID
      let entries = allEntries.filter(entry => entry.staffId === staffId);

      // Filter by period
      if (period !== 'all') {
        const now = new Date();
        let startDate;

        switch (period) {
          case 'current':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          case 'last_month':
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
            entries = entries.filter(entry => {
              const entryDate = new Date(entry.timestamp);
              return entryDate >= startDate && entryDate <= endDate;
            });
            return entries;
          case 'ytd':
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
          default:
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        entries = entries.filter(entry => {
          const entryDate = new Date(entry.timestamp);
          return entryDate >= startDate;
        });
      }

      return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (error) {
      console.error('Error fetching ledger entries:', error);
      throw error;
    }
  }

  // ==================== PAYROLL INTEGRATION ====================

  /**
   * Generate payouts for all staff with advanced compensation plans
   */
  async generatePayouts(payPeriod) {
    try {
      const assignments = await this.getStaffAssignments();
      const payouts = [];

      for (const assignment of assignments) {
        if (!assignment.isActive) continue;

        const compensation = await this.calculateStaffCompensation(assignment.staffId, payPeriod);
        
        if (compensation.total > 0) {
          const payout = {
            staffId: assignment.staffId,
            planId: assignment.planId,
            period: payPeriod,
            amount: compensation.total,
            breakdown: compensation.breakdown,
            status: 'pending',
            generatedAt: new Date()
          };

          const payoutId = await tenantDataService.create(this.payoutsCollection, payout);
          payouts.push({ ...payout, id: payoutId });
        }
      }

      return payouts;
    } catch (error) {
      console.error('Error generating payouts:', error);
      throw error;
    }
  }

  /**
   * Get payouts for a specific period or staff member
   */
  async getPayouts(filters = {}) {
    try {
      let payouts = await tenantDataService.getAll(this.payoutsCollection);

      if (filters.staffId) {
        payouts = payouts.filter(payout => payout.staffId === filters.staffId);
      }

      if (filters.period) {
        payouts = payouts.filter(payout => payout.period === filters.period);
      }

      if (filters.status) {
        payouts = payouts.filter(payout => payout.status === filters.status);
      }

      return payouts.sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt));
    } catch (error) {
      console.error('Error fetching payouts:', error);
      throw error;
    }
  }

  /**
   * Update payout status (e.g., mark as paid)
   */
  async updatePayoutStatus(payoutId, status, notes = '') {
    try {
      return await tenantDataService.update(this.payoutsCollection, payoutId, {
        status,
        notes,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating payout status:', error);
      throw error;
    }
  }

  // ==================== DEFAULT PLANS ====================

  /**
   * Create default compensation plans for a new tenant
   */
  async createDefaultPlans() {
    try {
      const defaultPlans = [
        {
          name: 'Standard Hourly',
          type: 'hourly',
          hourlyRate: 25,
          description: 'Standard hourly rate for staff members',
          isDefault: true
        },
        {
          name: 'Senior Clinician',
          type: 'hybrid',
          baseSalary: 4000,
          commissionRate: 15,
          description: 'Base salary plus commission for senior clinicians',
          isDefault: false
        },
        {
          name: 'Commission Only',
          type: 'commission',
          commissionRate: 30,
          description: 'Pure commission-based compensation',
          isDefault: false
        }
      ];

      const createdPlans = [];
      for (const plan of defaultPlans) {
        const planId = await this.createCompensationPlan(plan);
        createdPlans.push({ ...plan, id: planId });
      }

      return createdPlans;
    } catch (error) {
      console.error('Error creating default plans:', error);
      throw error;
    }
  }
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Check if advanced compensation plans are enabled for the current tenant
 */
export async function isAdvancedPlansEnabled() {
  try {
    const settings = await tenantDataService.getById('settings', 'clinic');
    return settings?.compensation?.enableAdvancedPlans === true;
  } catch (error) {
    console.error('Error checking advanced plans status:', error);
    return false;
  }
}

/**
 * Write a ledger entry (wrapper function for external imports)
 */
export async function writeLedgerEntry(staffId, entryData) {
  return await compPlanService.writeLedgerEntry(staffId, entryData);
}

/**
 * Build payouts for a specific period (wrapper function for external imports)
 */
export async function buildPayoutsForPeriod(year, month) {
  try {
    console.log(`🔄 Building payouts for ${year}-${month}...`);
    
    const assignments = await compPlanService.getStaffAssignments();
    const staff = await tenantDataService.getAll('staff');
    const payouts = [];

    for (const assignment of assignments) {
      if (!assignment.isActive) continue;

      const staffMember = staff.find(s => s.id === assignment.staffId);
      if (!staffMember) continue;

      const compensation = await compPlanService.calculateStaffCompensation(assignment.staffId, 'current');
      
      if (compensation.total > 0) {
        const payout = {
          staffId: assignment.staffId,
          staffName: `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim() || staffMember.name || 'Unknown',
          staffEmail: staffMember.email || '',
          role: staffMember.role || 'staff',
          planId: assignment.planId,
          year: year,
          month: month,
          appointmentCount: 0, // Will be calculated from ledger
          totalPayout: compensation.total,
          breakdown: compensation.breakdown,
          status: 'pending',
          generatedAt: new Date()
        };

        payouts.push(payout);
      }
    }

    console.log(`✅ Generated ${payouts.length} payouts for advanced compensation`);
    return payouts;
  } catch (error) {
    console.error('Error building payouts for period:', error);
    throw error;
  }
}

/**
 * Create default compensation plans from existing role rates
 */
export async function createDefaultPlansFromRoleRates() {
  try {
    const settings = await tenantDataService.getById('settings', 'clinic');
    const roleRates = settings?.roleRates || {};
    
    const defaultPlans = [];
    
    // Create plans based on existing role rates
    for (const [role, rate] of Object.entries(roleRates)) {
      if (rate && rate > 0) {
        const plan = {
          name: `${role.charAt(0).toUpperCase() + role.slice(1)} Default`,
          type: 'hourly',
          hourlyRate: rate,
          description: `Default hourly rate for ${role} role`,
          isDefault: true,
          createdFromRole: role
        };
        
        const planId = await compPlanService.createCompensationPlan(plan);
        defaultPlans.push({ ...plan, id: planId });
      }
    }
    
    // If no role rates exist, create standard defaults
    if (defaultPlans.length === 0) {
      return await compPlanService.createDefaultPlans();
    }
    
    return defaultPlans;
  } catch (error) {
    console.error('Error creating default plans from role rates:', error);
    throw error;
  }
}

/**
 * Auto-assign default plans to existing staff based on their roles
 */
export async function autoAssignDefaultPlans() {
  try {
    const staff = await tenantDataService.getAll('staff');
    const plans = await tenantDataService.getAll('compensationPlans');
    
    const assignments = [];
    
    for (const staffMember of staff) {
      // Find a plan that matches their role
      const matchingPlan = plans.find(plan => 
        plan.createdFromRole === staffMember.role || 
        (plan.isDefault && plan.name.toLowerCase().includes(staffMember.role?.toLowerCase()))
      );
      
      if (matchingPlan) {
        await compPlanService.assignPlanToStaff(staffMember.id, matchingPlan.id);
        assignments.push({
          staffId: staffMember.id,
          staffName: `${staffMember.firstName} ${staffMember.lastName}`,
          planId: matchingPlan.id,
          planName: matchingPlan.name
        });
      }
    }
    
    return assignments;
  } catch (error) {
    console.error('Error auto-assigning default plans:', error);
    throw error;
  }
}

/**
 * Create a compensation plan (wrapper function for external imports)
 */
export async function createCompensationPlan(planData) {
  return await compPlanService.createCompensationPlan(planData);
}

/**
 * Update a compensation plan (wrapper function for external imports)
 */
export async function updateCompensationPlan(planId, updates) {
  return await compPlanService.updateCompensationPlan(planId, updates);
}

// Export singleton instance
export const compPlanService = new CompPlanService();
export default compPlanService;
